import AddIcon from '@mui/icons-material/Add';
import AllOutIcon from '@mui/icons-material/AllOut';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ConditionalMenuItem from 'components/ConditionalMenuItem';
import ImageUpload from 'modules/project/PourDetailsPage/PourDetailsStepper/ImageUpload';
import CustomReportCompletionPanel from 'modules/project/CustomReports/CustomReportCompletionPanel';
import ProjectAuthorise from 'modules/project/ProjectAuthorise';
import { Trans } from '@lingui/react';
import { i18n } from 'i18n';
import { useSnackbar } from 'notistack';
import {
  CustomUnitFields,
  CustomReportRequirementStage,
  CustomReportTargetType,
  FileParameter,
  ProjectDto,
  ProjectModule,
  UnitSystem,
  useAddDocumentsToStopeMutation,
  useCustomReportRequirementsQuery,
  useMarkStopeAsCompleteMutation,
  useMarkStopeAsIncompleteMutation,
  useUpdateStopeGeometryFile
} from 'providers/api';
import { Permission } from 'providers/authorisation';
import { useProject } from 'modules/project/providers/project';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { withCurrentSearch } from '../Scenes/scene-utils';
import { convertVolume } from '../canvas/Model/Telemetry/conversions';
import StopeProgress from '../StopeProgress';
import useDeleteStope from '../useDeleteStope';
import DufStopeGeometryDialog from './DufStopeGeometryDialog';
import useDufStopeGeometryImport from './useDufStopeGeometryImport';
import { Model } from '../hooks/useModelState';

const GEOMETRY_MAX_FILE_SIZE_MB = 100;

interface StopeActionsMenuProps {
  stopeId: string;
  nodeId: string;
  hasIncompletePours: boolean;
  hasIncompleteCustomReports: boolean;
  hasGeometry: boolean;
  stopeComplete: boolean;

  onEdit: (nodeId: string) => void;
  onAddStope?: (nodeId: string) => void;
  onDelete: () => void;
  onMarkStopeAsComplete: () => void;
  onMarkStopeAsIncomplete: () => void;
  onStopeGeometryFileAdd: (obj: File) => void;
  onStopeFillView: () => void;
  onStopeDocumentUpload: (files: File[]) => void;
}

const StopeActionMenu = ({
  stopeId,
  nodeId,
  hasIncompletePours,
  hasIncompleteCustomReports,
  hasGeometry,
  stopeComplete,
  onEdit,
  onAddStope,
  onDelete,
  onMarkStopeAsComplete,
  onMarkStopeAsIncomplete,
  onStopeGeometryFileAdd,
  onStopeFillView,
  onStopeDocumentUpload,
}: StopeActionsMenuProps) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const { enqueueSnackbar } = useSnackbar();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleEdit = () => {
    onEdit(nodeId);
    setAnchorEl(null);
  };

  const handleAddStope = () => {
    onAddStope?.(nodeId);
    setAnchorEl(null);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMarkAsComplete = () => {
    onMarkStopeAsComplete();
    setAnchorEl(null);
  };

  const handleMarkAsIncomplete = () => {
    onMarkStopeAsIncomplete();
    setAnchorEl(null);
  };

  const handleStopeGeometryFileAdd = (obj: File) => {
    onStopeGeometryFileAdd(obj);
    setAnchorEl(null);
  };

  const handleStopeFillView = () => {
    onStopeFillView();
    setAnchorEl(null);
  };

  const handleDelete = () => {
    onDelete();
    setAnchorEl(null);
  };

  let markCompleteDisabledReason = i18n._('Unable to complete stope has outstanding Pours');
  if (stopeComplete) {
    markCompleteDisabledReason = i18n._('Stope is complete');
  } else if (hasIncompleteCustomReports) {
    markCompleteDisabledReason = i18n._('Required custom reports are incomplete');
  }

  return (
    <>
      <IconButton
        color="inherit"
        aria-controls={`project-actions-menu-${stopeId}`}
        aria-haspopup="true"
        onClick={handleClick}
        size="large"
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        id={`project-actions-menu-${stopeId}`}
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <ProjectAuthorise permission={Permission.ManagePours}>
          <ProjectAuthorise permission={Permission.ManageStopeData}>
            <MenuItem onClick={handleEdit}>
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <Typography variant="inherit"><Trans id="Edit Stope">Edit Stope</Trans></Typography>
            </MenuItem>
            {onAddStope && (
              <MenuItem onClick={handleAddStope}>
                <ListItemIcon>
                  <AddIcon fontSize="small" />
                </ListItemIcon>
                <Typography variant="inherit"><Trans id="Add Stope">Add Stope</Trans></Typography>
              </MenuItem>
            )}
          </ProjectAuthorise>
          <ProjectAuthorise module={ProjectModule.StopeFill}>
            <MenuItem>
              <ListItemIcon>
                <AllOutIcon fontSize="small" />
              </ListItemIcon>
              <ImageUpload
                onNewImage={(file) => {
                  if (Array.isArray(file)) {
                    handleStopeGeometryFileAdd(file[0]); // or ignore if empty
                  } else {
                    handleStopeGeometryFileAdd(file);
                  }
                }}
                label={hasGeometry ? i18n._('Update Geometry (.obj, .dxf, .duf)') : i18n._('Add Geometry (.obj, .dxf, .duf)')}
                accept=".obj,.dxf,.duf"
                maxFileSizeMB={GEOMETRY_MAX_FILE_SIZE_MB}
                onFileSizeError={() => {
                  enqueueSnackbar(i18n._('Geometry files must be {maxSize} MB or smaller.', { maxSize: GEOMETRY_MAX_FILE_SIZE_MB }), { variant: 'error' });
                  setAnchorEl(null);
                }}
              />
            </MenuItem>
            <ConditionalMenuItem
              disabled={!hasGeometry}
              disabledReason={i18n._('No geometry has been added')}
              onClick={handleStopeFillView}
            >
              <ListItemIcon>
                <AssignmentTurnedInIcon fontSize="small" />
              </ListItemIcon>
              <Typography variant="inherit"><Trans id="Stope Fill View">Stope Fill View</Trans></Typography>
            </ConditionalMenuItem>
          </ProjectAuthorise>

          <ConditionalMenuItem
            disabled={hasIncompletePours || hasIncompleteCustomReports || stopeComplete}
            disabledReason={markCompleteDisabledReason}
            onClick={handleMarkAsComplete}
          >
            <ListItemIcon>
              <AssignmentTurnedInIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit"><Trans id="Mark Stope Complete">Mark Stope Complete</Trans></Typography>
          </ConditionalMenuItem>

          <ConditionalMenuItem
            disabled={!stopeComplete}
            disabledReason={i18n._('Stope is incomplete')}
            onClick={handleMarkAsIncomplete}
          >
            <ListItemIcon>
              <SettingsBackupRestoreIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit"><Trans id="Mark Stope Incomplete">Mark Stope Incomplete</Trans></Typography>
          </ConditionalMenuItem>
        </ProjectAuthorise>
        <ProjectAuthorise permission={Permission.ManageStopeData}>
          <MenuItem onClick={handleDelete}>
            <ListItemIcon>
              <DeleteForeverIcon color="error" fontSize="small" />
            </ListItemIcon>
            <Typography color="error" variant="inherit"><Trans id="Delete Stope">Delete Stope</Trans></Typography>
          </MenuItem>
          <MenuItem>
            <ListItemIcon>
              <UploadFileIcon fontSize="small" />
            </ListItemIcon>
            <ImageUpload
              label={i18n._('Upload Document')}
              accept=".pdf,.doc,.docx,.xlsx,.xls,.csv,.txt"
              multiple
              maxFileSizeMB={10}
              onNewImage={(files) => {
                onStopeDocumentUpload(Array.isArray(files) ? files : [files]);
                setAnchorEl(null);
              }}
              onFileSizeError={() => {
                // eslint-disable-next-line no-alert -- intentional native notice for oversized uploads
                alert(i18n._('Some files exceed the 10MB size limit.'));
              }}
            />
          </MenuItem>
        </ProjectAuthorise>
      </Menu>
    </>
  );
};

interface StopeTitleCardProps {
  stopeId: string;
  stopeName: string;
  completedVolume: number;
  plannedVolume: number;
  volume?: number;
  stopeComplete: boolean;
  hasIncompletePours: boolean;
  hasGeometry: boolean;
  unitSystemPreference: UnitSystem;
  customUnitFields: CustomUnitFields[];
  nodeId: string;
  model: Model;
  onStopeEdit: (nodeId: string) => void;
  onAddStope?: (nodeId: string) => void;
  onDeleteSuccess?: (nodeId: string) => void;
  onGeometryUploaded?: () => void;
}

const StopeTitleCard = ({
  stopeId,
  stopeName,
  completedVolume,
  plannedVolume,
  volume,
  hasIncompletePours,
  hasGeometry,
  stopeComplete,
  unitSystemPreference,
  customUnitFields,
  nodeId,
  model,
  onStopeEdit,
  onAddStope,
  onDeleteSuccess,
  onGeometryUploaded,
}: StopeTitleCardProps) => {
  const { entityId } = useParams<{ entityId: ProjectDto['entityId'] }>();

  if (entityId === undefined) throw new Error("Required route parameter 'EntityId' was not found");

  const navigate = useNavigate();
  const deleteStopeMutation = useDeleteStope();
  const updateStopeGeometryFile = useUpdateStopeGeometryFile(entityId);
  const markStopeAsCompleteMutation = useMarkStopeAsCompleteMutation(entityId);
  const markStopeAsIncompleteMutation = useMarkStopeAsIncompleteMutation(entityId);
  const addDocumentsMutation = useAddDocumentsToStopeMutation(entityId);
  const { state: { enabledModules } } = useProject();
  const customReportsModuleEnabled = enabledModules.some((module) => module === ProjectModule.CustomReports);
  const { data: customReportRequirements = [] } = useCustomReportRequirementsQuery(
    { projectId: entityId, targetType: CustomReportTargetType.Stope, targetId: stopeId },
    { enabled: Boolean(entityId && stopeId) && customReportsModuleEnabled },
  );
  const stopeCompletionRequirements = customReportRequirements.filter(
    (requirement) => requirement.stage === CustomReportRequirementStage.BeforeStopeCompletion,
  );
  const hasIncompleteCustomReports = customReportsModuleEnabled
    && stopeCompletionRequirements.some((requirement) => !requirement.isComplete);
  const [geometryUploaded, setGeometryUploaded] = React.useState(false);
  const handleGeometryUploadSuccess = React.useCallback(() => {
    setGeometryUploaded(true);
    onGeometryUploaded?.();
  }, [onGeometryUploaded]);
  const dufImport = useDufStopeGeometryImport({
    entityId,
    stopeId,
    model,
    onImportSuccess: handleGeometryUploadSuccess,
  });
  const effectiveHasGeometry = hasGeometry || geometryUploaded;

  const handleDelete = () => {
    if (!stopeId) return;

    deleteStopeMutation(
      entityId,
      nodeId,
      stopeId,
      stopeName,
      (deletedNodeId) => {
        onDeleteSuccess?.(deletedNodeId);
      },
    );
  };

  const handleMarkAsComplete = () => {
    if (stopeId) {
      markStopeAsCompleteMutation.mutate(stopeId);
    }
  };

  const handleMarkAsIncomplete = () => {
    if (stopeId) {
      markStopeAsIncompleteMutation.mutate(stopeId);
    }
  };

  const handleStopeGeometryFileAdd = (obj: File) => {
    const extension = obj.name.split('.').pop()?.toLowerCase();
    if (extension === 'duf') {
      dufImport.handleDufFileSelected(obj);
    } else {
      updateStopeGeometryFile.mutate(
        { stopeId, file: { data: obj, fileName: obj.name } },
        { onSuccess: handleGeometryUploadSuccess },
      );
    }
  };

  const handleStopeFillView = () => {
    const currentPath = window.location.pathname;
    const pathParts = currentPath.split('/').filter(Boolean);
    const pathWithoutBasename = pathParts[0] === 'app' ? pathParts.slice(1) : pathParts;
    const overviewIndex = pathWithoutBasename.findIndex((part) => part === 'overview');

    if (overviewIndex !== -1) {
      const basePath = `/${pathWithoutBasename.slice(0, overviewIndex + 1).join('/')}`;
      navigate(withCurrentSearch(`${basePath}/stopes/${stopeId}/fill`), { replace: true });
    }
  };

  const handleDocumentUpload = (files: File[]) => {
    const fileParams: FileParameter[] = files.map((file) => ({
      data: file,
      fileName: file.name,
    }));

    addDocumentsMutation.mutate({
      stopeId,
      files: fileParams,
    });
  };

  const isImperial = unitSystemPreference === UnitSystem.Imperial;

  return (
    <>
      <Card>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 1,
          }}
        >
          <Tooltip
            placement="left"
            arrow
            title={(
              <>
                <Typography variant="subtitle2">{i18n._('Total:').concat(`${convertVolume(volume as number, isImperial, customUnitFields)}`)}</Typography>
                <Typography variant="subtitle2">{i18n._('Placed:').concat(`${convertVolume(completedVolume as number, isImperial, customUnitFields)}`)}</Typography>
                <Typography variant="subtitle2">{i18n._('Planned:').concat(`${convertVolume(plannedVolume as number, isImperial, customUnitFields)}`)}</Typography>
              </>
            )}
          >
            <div>
              <StopeProgress completedVolume={completedVolume} plannedVolume={plannedVolume} size={75} volume={volume} />
            </div>
          </Tooltip>
          <Box
            sx={{
              flexGrow: 1,
              ml: 3,
            }}
          >
            <Typography variant="h6" style={{ wordWrap: 'anywhere' as any }}>{stopeName}</Typography>
          </Box>
          <StopeActionMenu
            stopeId={stopeId}
            nodeId={nodeId}
            onEdit={onStopeEdit}
            onAddStope={onAddStope}
            onDelete={handleDelete}
            onMarkStopeAsComplete={handleMarkAsComplete}
            onMarkStopeAsIncomplete={handleMarkAsIncomplete}
            onStopeGeometryFileAdd={handleStopeGeometryFileAdd}
            onStopeFillView={handleStopeFillView}
            stopeComplete={stopeComplete}
            hasIncompletePours={hasIncompletePours}
            hasIncompleteCustomReports={hasIncompleteCustomReports}
            hasGeometry={effectiveHasGeometry}
            onStopeDocumentUpload={handleDocumentUpload}
          />
        </Box>
      </Card>
      <ProjectAuthorise module={ProjectModule.CustomReports}>
        <Box sx={{ mt: 1 }}>
          <CustomReportCompletionPanel
            projectId={entityId}
            targetType={CustomReportTargetType.Stope}
            targetId={stopeId}
            stage={CustomReportRequirementStage.BeforeStopeCompletion}
            hideWhenEmpty
          />
        </Box>
      </ProjectAuthorise>
      <DufStopeGeometryDialog
        open={dufImport.dialogOpen}
        isParsing={dufImport.isParsing}
        isImporting={dufImport.isImporting}
        error={dufImport.error}
        isImperial={dufImport.isImperial}
        layers={dufImport.layersWithGeometry}
        availableAttributes={dufImport.availableAttributes}
        uniqueValues={dufImport.uniqueValues}
        matchingEntities={dufImport.matchingEntities}
        selectedLayerGuid={dufImport.selectedLayerGuid}
        selectedAttribute={dufImport.selectedAttribute}
        selectedValue={dufImport.selectedValue}
        onLayerChange={dufImport.handleLayerChange}
        onAttributeChange={dufImport.handleAttributeChange}
        onValueChange={dufImport.handleValueChange}
        onImport={dufImport.handleImport}
        onCancel={dufImport.handleCancel}
      />
    </>
  );
};

export default StopeTitleCard;
